import { Component, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

@Component({
  standalone: true,
  selector: 'fun-run-scheduler',
  imports: [CommonModule, FormsModule],
  encapsulation: ViewEncapsulation.ShadowDom,
  template: `
    <style>
      :host {
        display: block;
        color: var(--bs-gray-800, #343a40);
        font: inherit;
      }
      .card {
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: 1rem;
        padding: clamp(1rem, 3vw, 2.5rem);
        background: var(--bs-white, #fff);
      }
      h2 {
        margin: 0 0 1.5rem;
        color: var(--bs-gray-900, #212529);
        font-size: clamp(1.5rem, 3vw, 1.875rem);
        line-height: 1.2;
      }
      h3 {
        margin: 2rem 0 0.75rem;
        color: var(--bs-gray-900, #212529);
        font-size: 1.25rem;
      }
      .row {
        display: flex;
        flex-wrap: wrap;
        gap: 1rem;
      }
      .row + .row {
        margin-top: 1rem;
      }
      .field {
        flex: 1 1 220px;
      }
      label {
        display: block;
        margin-bottom: 0.375rem;
        font-weight: 600;
      }
      input,
      select,
      button,
      textarea {
        width: 100%;
        min-height: 2.75rem;
        padding: 0.625rem 0.75rem;
        border: 1px solid var(--bs-border-color, #ced4da);
        border-radius: 0.5rem;
        background: var(--bs-white, #fff);
        color: var(--bs-gray-900, #212529);
        font: inherit;
      }
      input:focus-visible,
      select:focus-visible,
      button:focus-visible,
      textarea:focus-visible {
        outline: 0.2rem solid rgba(var(--bs-primary-rgb, 31, 120, 180), 0.24);
        outline-offset: 0.125rem;
      }
      button {
        min-height: 3rem;
        border-color: var(--bs-primary, #1f78b4);
        background: var(--bs-primary, #1f78b4);
        color: var(--bs-white, #fff);
        cursor: pointer;
        font-weight: 700;
        transition:
          background-color 160ms ease,
          border-color 160ms ease;
      }
      button:hover {
        border-color: #196391;
        background: #196391;
      }
      ul {
        margin: 0;
        padding: 1rem 1rem 1rem 2.25rem;
        border: 1px solid var(--bs-border-color, #dee2e6);
        border-radius: 0.75rem;
        background: var(--bs-light, #f8f9fa);
        line-height: 1.65;
      }
      li + li {
        margin-top: 0.375rem;
      }
      @media (prefers-reduced-motion: reduce) {
        button {
          transition: none;
        }
      }
    </style>

    <div class="card">
      <h2>Create a Fun-Run Schedule</h2>
      <div class="row">
        <div class="field">
          <label for="funRunEventDate">Event Date</label>
          <input id="funRunEventDate" type="date" [(ngModel)]="eventDate" />
        </div>
        <div class="field">
          <label for="funRunHeatMinutes">Heat Duration (min)</label>
          <input
            id="funRunHeatMinutes"
            type="number"
            [(ngModel)]="heatMinutes"
            min="5"
          />
        </div>
        <div class="field">
          <label for="funRunNumHeats">Number of Heats</label>
          <input
            id="funRunNumHeats"
            type="number"
            [(ngModel)]="numHeats"
            min="1"
          />
        </div>
      </div>

      <div class="row">
        <div class="field">
          <label for="funRunBreakMinutes">Break Between Heats (min)</label>
          <input
            id="funRunBreakMinutes"
            type="number"
            [(ngModel)]="breakMinutes"
            min="0"
          />
        </div>
        <div class="field">
          <label for="funRunStartTime">Start Time</label>
          <input id="funRunStartTime" type="time" [(ngModel)]="startTime" />
        </div>
      </div>

      <div class="row">
        <button (click)="generate()">Generate Schedule</button>
      </div>

      <div *ngIf="schedule?.length">
        <h3>Schedule</h3>
        <ul>
          <li *ngFor="let s of schedule">{{ s }}</li>
        </ul>
      </div>
    </div>
  `,
})
export class FunRunSchedulerComponent {
  eventDate = '';
  startTime = '09:00';
  heatMinutes = 15;
  breakMinutes = 5;
  numHeats = 6;
  schedule: string[] = [];

  generate() {
    if (!this.eventDate || !this.startTime) {
      this.schedule = [];
      return;
    }
    const [h, m] = this.startTime.split(':').map(Number);
    const base = new Date(
      `${this.eventDate}T${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`,
    );
    const out: string[] = [];
    for (let i = 0; i < this.numHeats; i++) {
      const start = new Date(
        base.getTime() + i * (this.heatMinutes + this.breakMinutes) * 60000,
      );
      const end = new Date(start.getTime() + this.heatMinutes * 60000);
      out.push(
        `Heat ${i + 1}: ${start.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} – ${end.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`,
      );
    }
    this.schedule = out;
    window.dispatchEvent(
      new CustomEvent('tools:funrun:generated', {
        detail: { count: this.schedule.length },
      }),
    );
  }
}
